import { clone } from 'common/utils/generic';
import { isArray } from 'lodash';
import { AddedSection, AddedAttribute, AddedAttributeType } from '../types';

export type SetUserAttributeTypes = (userAttributeTypes: AddedAttributeType[]) => void;
export type SetAddedSections = (addedSections: AddedSection[]) => void;

// TODO: combine this with updateValue. These seem to be doing the same thing
// except how they handle when the type is not found.
export const updateSingletonValue = (
  userAttributeTypes: AddedAttributeType[],
  setUserAttributeTypes: SetUserAttributeTypes,
  attributeTypeName: string,
  value: AddedAttribute | undefined
): void => {
  const userAttributeTypesCopy = clone(userAttributeTypes);
  const attributeType = userAttributeTypesCopy.find(type => type.name === attributeTypeName);
  if (!attributeType)
    throw new Error(`An error occurred, ${attributeTypeName} does not seem to exist`);
  attributeType.attributes = value ? [value] : [];
  setUserAttributeTypes(userAttributeTypesCopy);
};

/**
 * This function creates a new copy of the `userAttributeTypes` array
 * and updates the attributes of the attribute type with the given
 * `attributeTypeName` based on the provided `value`.
 *
 * The `setUserAttributeTypes` function is called with the updated copy
 * of `userAttributeTypes`.
 */
export const updateValue = (
  attributeTypeName: string,
  value: AddedAttribute | AddedAttribute[] | null,
  userAttributeTypes: AddedAttributeType[],
  setUserAttributeTypes: SetUserAttributeTypes
) => {
  const userAttributeTypesCopy = clone(userAttributeTypes);
  const attributeTypeCopy = userAttributeTypesCopy.find(type => type.name === attributeTypeName);

  // if cannot find the attribute type, do nothing
  if (!attributeTypeCopy) return;

  // if the value is null, set the attributes to be an empty array
  if (!value) {
    attributeTypeCopy.attributes = [];
    setUserAttributeTypes(userAttributeTypesCopy);
  } else {
    attributeTypeCopy.attributes = isArray(value) ? [...value] : [value];
    setUserAttributeTypes(userAttributeTypesCopy);
  }
};

export const updateAddedValue = (
  sectionName: string,
  attributeTypeName: string,
  inputValue: string | string[] | null,
  addedSections: AddedSection[],
  userAttributeTypes: AddedAttributeType[],
  setUserAttributeTypes: SetUserAttributeTypes,
  setAddedSections: SetAddedSections
) => {
  const newAddedSections = clone(addedSections);
  let addedAttributesArray: AddedAttribute[];
  if (inputValue && inputValue instanceof Array) {
    addedAttributesArray = inputValue.map(value => ({ value }));
  } else {
    addedAttributesArray = inputValue ? [{ value: inputValue }] : [];
  }
  let newSection = newAddedSections.find(addedSection => addedSection.name === sectionName);
  let newAttributeType =
    newSection && newSection.attribute_types.find(attrType => attrType.name === attributeTypeName);
  if (newSection && newAttributeType) {
    newAttributeType.attributes = addedAttributesArray;
  } else if (newSection) {
    newAttributeType = { name: attributeTypeName, attributes: addedAttributesArray };
    newSection.attribute_types.push(newAttributeType);
  } else if (inputValue) {
    newSection = {
      name: sectionName,
      addedSection: true,
      attribute_types: [
        {
          name: attributeTypeName,
          attributes: addedAttributesArray,
        },
      ],
    };
    newAddedSections.push(newSection);
  }

  updateValue(attributeTypeName, addedAttributesArray, userAttributeTypes, setUserAttributeTypes);
  setAddedSections(newAddedSections);
};

export const updateSingletonStartOrEndTime = (
  attributeTypeName: string,
  userAttributeTypes: AddedAttributeType[],
  setUserAttributeTypes: SetUserAttributeTypes,
  attributeValue: string | number,
  isStartDate: boolean,
  dateValue?: string,
  currentStartDate?: string,
  currentEndDate?: string
) => {
  if (isStartDate && currentEndDate && dateValue && dateValue >= currentEndDate) {
    throw new Error('Cannot set start date to be later than the end date of an attribute');
  }
  if (!isStartDate && currentStartDate && dateValue && dateValue <= currentStartDate) {
    throw new Error('Cannot set end date to be earlier than the start date of an attribute');
  }

  const newUserAttributeTypes = clone(userAttributeTypes);
  const targetUserAttributeType = newUserAttributeTypes.find(
    userAttributeType => userAttributeType.name === attributeTypeName
  );
  if (!targetUserAttributeType) {
    console.error(`trying to edit attribute ${attributeValue} for ${attributeTypeName}`);
    throw new Error(`Could not find attribute type ${attributeTypeName}`);
  }
  const targetAttribute = targetUserAttributeType.attributes.find(
    attribute =>
      attribute.value === attributeValue &&
      attribute.start_date === currentStartDate &&
      attribute.end_date === currentEndDate
  );
  if (!targetAttribute) {
    console.error(`Could not find attribute id ${attributeValue} on ${attributeTypeName}`);
    throw new Error(`Could not find attribute ${attributeValue} to edit`);
  }

  if (isStartDate) {
    targetAttribute.start_date = dateValue;
  } else {
    targetAttribute.end_date = dateValue;
  }
  setUserAttributeTypes(newUserAttributeTypes);
};
